import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  PlusCircleIcon,
  LockClosedIcon,
  UsersIcon,
  ClockIcon,
  FireIcon,
  SignalIcon,
  MapIcon,
  CubeIcon,
  BuildingOfficeIcon,
  AcademicCapIcon,
  ShoppingBagIcon,
  WrenchScrewdriverIcon,
  BuildingStorefrontIcon,
  BuildingOffice2Icon,
  CogIcon
} from "@heroicons/react/24/outline";

const AILandingPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const els = document.querySelectorAll('.ai-container .reveal');
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); } });
    }, { threshold: 0 });
    els.forEach(el => io.observe(el));
    return () => {
      els.forEach(el => io.unobserve(el));
    };
  }, []);

  const handleBookDemo = (e) => {
    e?.preventDefault();
    sessionStorage.setItem('demoSource', 'AI Landing Page — Vigil AI & Aakash');
    navigate('/audit#contact');
  };

  return (
    <div className="ai-container">
      <style>{`
        
  .ai-container {

    --void: #ffffff;
    --panel: #f8fafc;
    --panel-2: #f1f5f9;
    --line: #e2e8f0;
    --line-soft: #f1f5f9;
    --violet: #8b5cf6;
    --violet-dim: #7c3aed;
    --magenta: #d51776;
    --sky: #3b82f6;
    --amber: #f59e0b;
    --text: #0f172a;
    --text-dim: #334155;
    --text-faint: #64748b;
    --grad-brand: linear-gradient(120deg, #8b5cf6 0%, #d51776 100%);

}
  *{box-sizing:border-box; margin:0; padding:0;}
  html{scroll-behavior:smooth;}
  .ai-container{
    background:var(--void);
    color:var(--text);
    font-family:'Inter', sans-serif;
    line-height:1.5;
    overflow-x:hidden;
    -webkit-font-smoothing:antialiased;
  }
  ::selection{ background:var(--violet); color:#fff; }
  a{color:inherit; text-decoration:none;}
  img{max-width:100%; display:block;}
  .wrap{ max-width:1240px; margin:0 auto; padding:0 32px; }
  h1,h2,h3,h4{ font-family:'Space Grotesk', sans-serif; letter-spacing:-0.01em; }
  .eyebrow{
    font-family:'JetBrains Mono', monospace;
    font-size:12px; letter-spacing:0.14em; text-transform:uppercase;
    color:var(--text-faint); display:flex; align-items:center; gap:10px;
  }
  .eyebrow::before{ content:''; width:18px; height:1px; background:var(--text-faint); display:inline-block; }
  .eyebrow.sky{ color:#8fd9ff; } .eyebrow.sky::before{ background:#8fd9ff; }
  .eyebrow.ground{ color:#d99ee6; } .eyebrow.ground::before{ background:#d99ee6; }

  /* ---------- NAV ---------- */
  header{
    position:fixed; top:0; left:0; right:0; z-index:100;
    backdrop-filter: blur(14px);
    background:rgba(5,6,13,0.72);
    border-bottom:1px solid var(--line-soft);
  }
  nav.wrap{ display:flex; align-items:center; justify-content:space-between; height:76px; }
  .logo{ display:flex; align-items:center; gap:10px; font-family:'Space Grotesk'; font-weight:700; font-size:19px; }
  .logo .mark{
    width:30px; height:30px; border-radius:9px;
    background:conic-gradient(from 200deg, #ff8a3d, #e0369b, #7c5cff, #3fd6ff, #ff8a3d);
    position:relative;
  }
  .logo .mark::after{
    content:''; position:absolute; inset:6px; border-radius:6px; background:var(--void);
    clip-path: polygon(0 0, 55% 0, 55% 100%, 0 100%);
  }
  .logo small{ display:block; font-family:'Inter'; font-weight:500; font-size:9.5px; letter-spacing:0.09em; color:var(--text-faint); text-transform:uppercase; margin-top:1px;}
  .navlinks{ display:flex; gap:34px; font-size:14px; color:var(--text-dim); }
  .navlinks a{ transition:color .2s; }
  .navlinks a:hover{ color:var(--text); }
  .nav-cta{
    font-size:13.5px; font-weight:600; padding:10px 20px; border-radius:100px;
    background:var(--text); color:var(--void);
    transition:transform .18s ease;
  }
  .nav-cta:hover{ transform:translateY(-2px); }
  @media (max-width:860px){ .navlinks{ display:none; } }

  /* ---------- HERO ---------- */
  .hero{
    position:relative; padding:168px 0 90px; overflow:hidden;
  }
  .hero .eyebrow { color: rgba(255,255,255,0.8); }
  .hero .eyebrow::before { background: rgba(255,255,255,0.8); }
  .horizon{
    position:absolute; left:0; right:0; top:0; height:1px;
    background:linear-gradient(90deg, transparent, rgba(255,255,255,0.2) 15%, rgba(255,255,255,0.2) 85%, transparent);
  }
  .hero .wrap{ position:relative; }
  .hero-grid{ display:grid; grid-template-columns:1.1fr 0.9fr; gap:64px; align-items:center; }
  .hero h1{
    font-size:clamp(38px, 5vw, 64px); font-weight:700; line-height:1.03; letter-spacing:-0.02em;
    margin:22px 0 24px; color: #ffffff;
  }
  .hero h1 .g1{ background:linear-gradient(90deg,#d99ee6,#e0369b); -webkit-background-clip:text; background-clip:text; color:transparent; }
  .hero h1 .g2{ background:linear-gradient(90deg,#3fd6ff,#7c5cff); -webkit-background-clip:text; background-clip:text; color:transparent; }
  .hero p.lede{ font-size:17.5px; color:rgba(255,255,255,0.8); max-width:490px; margin-bottom:34px; }
  .hero-actions{ display:flex; gap:14px; align-items:center; flex-wrap:wrap; }
  .btn-primary{
    background: white; color: rgba(143, 3, 168, 1); font-weight:600; font-size:14.5px;
    padding:14px 26px; border-radius:100px; display:inline-flex; align-items:center; gap:8px;
    box-shadow:0 8px 30px -8px rgba(224,54,155,0.55);
    transition:transform .18s ease, box-shadow .18s ease;
  }
  .btn-primary:hover{ transform:translateY(-2px); box-shadow:0 12px 34px -6px rgba(224,54,155,0.65); }
  .btn-ghost{
    border:1px solid var(--line); color:var(--text); font-weight:600; font-size:14.5px;
    padding:14px 24px; border-radius:100px; transition:border-color .18s, background .18s;
  }
  .btn-ghost:hover{ border-color:var(--text-dim); background:var(--panel); }

  /* hero visual: split ground/sky dial */
  .hero-visual{
    position:relative; aspect-ratio:1/1; border-radius:26px; overflow:hidden;
    border:1px solid rgba(255, 255, 255, 0.15);
    background: rgba(255, 255, 255, 0.04);
    backdrop-filter: blur(24px);
    -webkit-backdrop-filter: blur(24px);
    box-shadow: inset 0 0 30px rgba(255, 255, 255, 0.05), 0 10px 40px rgba(0, 0, 0, 0.2);
  }
  .hero-visual .scanline{
    position:absolute; left:0; right:0; height:2px; top:52%;
    background:linear-gradient(90deg, transparent, var(--violet), var(--violet-dim), transparent);
    box-shadow:0 0 20px 3px rgba(139, 92, 246, 0.6);
    animation: scan 4s ease-in-out infinite;
    z-index: 3;
  }
  @keyframes scan{ 
    0%,100%{ top:10%; opacity:0.4; } 
    50%{ top:90%; opacity:1; box-shadow:0 0 25px 5px rgba(139, 92, 246, 0.8); } 
  }
  .hv-tag{
    position:absolute; font-family:'JetBrains Mono'; font-size:10.5px; padding:6px 12px;
    border-radius:8px; border:1px solid rgba(255, 255, 255, 0.15); 
    background:rgba(255, 255, 255, 0.08);
    backdrop-filter:blur(10px); letter-spacing:0.03em;
    color: #f1f5f9;
    box-shadow: 0 4px 15px rgba(0,0,0,0.1);
    transition: background 0.3s ease;
    z-index: 4;
  }
  .hv-tag:hover {
    background:rgba(255, 255, 255, 0.18);
  }
  .hv-tag b{ color:var(--amber); font-weight:600; }
  .hv-tag.ground b{ color:var(--amber); }
  .hv-tag.t1{ top:12%; left:8%; animation: float 6s ease-in-out infinite; } 
  .hv-tag.t2{ top:20%; right:9%; animation: float 7s ease-in-out infinite reverse; }
  .hv-tag.t3{ bottom:26%; left:10%; animation: float 6.5s ease-in-out infinite 1s; } 
  .hv-tag.t4{ bottom:15%; right:8%; animation: float 5.5s ease-in-out infinite reverse 1s; }
  @keyframes float {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-8px); }
  }
  .hv-core{
    position:absolute; top:50%; left:50%; transform:translate(-50%,-50%);
    width:120px; height:120px; border-radius:50%;
    border:1px solid rgba(139, 92, 246, 0.3);
    display:flex; align-items:center; justify-content:center;
    background:rgba(139, 92, 246, 0.15);
    backdrop-filter: blur(12px);
    box-shadow: 0 0 40px rgba(139, 92, 246, 0.25);
    z-index: 2;
  }
  .hv-core::before, .hv-core::after{
    content:''; position:absolute; border-radius:50%; border:1px solid rgba(139, 92, 246, 0.4);
  }
  .hv-core::before{ inset:-34px; animation: pulse-ring 3s infinite cubic-bezier(0.215, 0.61, 0.355, 1); }
  .hv-core::after{ inset:-68px; opacity:.6; animation: pulse-ring 3s infinite cubic-bezier(0.215, 0.61, 0.355, 1) 1.5s; }
  @keyframes pulse-ring {
    0% { transform: scale(0.9); opacity: 0; }
    50% { opacity: 0.6; }
    100% { transform: scale(1.15); opacity: 0; }
  }
  .hv-core span{ font-family:'Space Grotesk'; font-weight:700; font-size:16px; color: #ffffff; text-shadow: 0 0 12px rgba(139, 92, 246, 0.9); }
  .hv-label{
    position:absolute; font-family:'JetBrains Mono'; font-size:11px; letter-spacing:0.12em;
    text-transform:uppercase; color: #cbd5e1;
    z-index: 4;
  }
  .hv-label.sky{ top:20px; left:20px; color: #ddd6fe; }
  .hv-label.grd{ bottom:20px; left:20px; color: #ddd6fe; }

  /* ---------- STAT BAR ---------- */
  .statbar{
    border-top:1px solid var(--line-soft); border-bottom:1px solid var(--line-soft);
    background:var(--panel);
  }
  .statbar .wrap{ display:grid; grid-template-columns:repeat(4,1fr); }
  .stat{ padding:34px 26px; border-right:1px solid var(--line-soft); }
  .stat:last-child{ border-right:none; }
  .stat .num{ font-family:'Space Grotesk'; font-weight:700; font-size:32px; }
  .stat .num.pink{ color:var(--magenta); } .stat .num.blue{ color:var(--sky); }
  .stat .lbl{ font-size:13px; color:var(--text-dim); margin-top:6px; }
  @media (max-width:760px){ .statbar .wrap{ grid-template-columns:1fr 1fr; } .stat:nth-child(2){border-right:none;} }

  /* ---------- SECTION SHELL ---------- */
  section{ padding:120px 0; position:relative; }
  .sec-head{ max-width:640px; margin-bottom:60px; }
  .sec-head h2{ font-size:clamp(28px,3.4vw,42px); font-weight:700; margin-top:16px; line-height:1.12; }
  .sec-head p{ color:var(--text-dim); font-size:16px; margin-top:14px; max-width:560px; }

  /* GROUND section */
  #ground{ background:radial-gradient(ellipse 1000px 500px at 80% 0%, rgba(224,54,155,0.09), transparent 60%); }
  .card-grid{ display:grid; grid-template-columns:repeat(3,1fr); gap:1px; background:var(--line-soft); border:1px solid var(--line-soft); border-radius:18px; overflow:hidden; }
  .card{ background:var(--panel); padding:30px 28px; transition:background .2s; }
  .card:hover{ background:var(--panel-2); }
  .card .ic{ width:38px; height:38px; border-radius:10px; display:flex; align-items:center; justify-content:center; margin-bottom:18px; font-size:17px; }
  .card.g .ic{ background:rgba(149, 17, 179, 0.1); border:1px solid rgba(149, 17, 179, 0.3); }
  .card.s .ic{ background:rgba(149, 17, 179, 0.1); border:1px solid rgba(149, 17, 179, 0.3); }
  .card h4{ font-size:16px; font-weight:600; margin-bottom:9px; }
  .card p{ font-size:13.7px; color:var(--text-dim); line-height:1.55; }
  @media (max-width:860px){ .card-grid{ grid-template-columns:1fr; } }

  .split{ display:grid; grid-template-columns:0.85fr 1.15fr; gap:60px; align-items:flex-start; }
  @media (max-width:900px){ .split{ grid-template-columns:1fr; } }

  .metric-panel{
    border:1px solid var(--line); border-radius:18px; background:var(--panel);
    padding:26px; margin-top:8px;
  }
  .metric-row{ display:flex; justify-content:space-between; align-items:center; padding:14px 0; border-bottom:1px solid var(--line-soft); font-size:14px; }
  .metric-row:last-child{ border-bottom:none; }
  .metric-row .k{ color:var(--text-dim); }
  .metric-row .v{ font-family:'JetBrains Mono'; font-weight:500; }
  .metric-row .v.pink{ color:var(--magenta); } .metric-row .v.blue{ color:var(--sky); }

  /* SKY section */
  #sky{ background:radial-gradient(ellipse 1000px 500px at 20% 0%, rgba(63,214,255,0.09), transparent 60%); }

  /* Journey / phases */
  .phase-row{ display:grid; grid-template-columns:repeat(5,1fr); gap:18px; margin-top:10px; }
  .phase{ border:1px solid var(--line); border-radius:14px; padding:20px 18px; background:var(--panel); position:relative; }
  .phase .pn{ font-family:'JetBrains Mono'; font-size:11px; color:var(--text-faint); }
  .phase h5{ font-size:14.5px; margin:10px 0 6px; font-weight:600; }
  .phase p{ font-size:12.3px; color:var(--text-dim); line-height:1.5; }
  @media (max-width:960px){ .phase-row{ grid-template-columns:1fr 1fr; } }
  @media (max-width:560px){ .phase-row{ grid-template-columns:1fr; } }

  /* Proof section */
  #proof{ background:var(--panel); border-top:1px solid var(--line-soft); border-bottom:1px solid var(--line-soft); }
  .proof-grid{ display:grid; grid-template-columns:1.05fr 0.95fr; gap:60px; align-items:center; }
  @media (max-width:900px){ .proof-grid{ grid-template-columns:1fr; } }
  .quote-card{
    border:1px solid var(--line); border-radius:20px; padding:40px; background:var(--void);
  }
  .quote-card .who{ display:flex; align-items:center; gap:12px; margin-bottom:24px; }
  .who .dot{ width:10px; height:10px; border-radius:50%; background:var(--grad-brand); }
  .who span{ font-family:'JetBrains Mono'; font-size:12px; letter-spacing:0.08em; text-transform:uppercase; color:var(--text-faint); }
  .quote-card p.big{ font-size:19px; line-height:1.55; font-weight:500; }
  .kpi-mini{ display:grid; grid-template-columns:1fr 1fr; gap:18px; margin-top:28px; }
  .kpi-mini div{ border-top:1px solid var(--line-soft); padding-top:12px; }
  .kpi-mini .n{ font-family:'Space Grotesk'; font-weight:700; font-size:22px; color:var(--sky); }
  .kpi-mini .l{ font-size:12px; color:var(--text-dim); margin-top:3px; }

  .proof-list{ display:flex; flex-direction:column; gap:0; }
  .proof-item{ display:flex; gap:18px; padding:20px 0; border-bottom:1px solid var(--line-soft); }
  .proof-item:first-child{ padding-top:0; }
  .proof-item .num{ font-family:'Space Grotesk'; font-weight:700; font-size:20px; color:var(--text-faint); width:34px; flex-shrink:0; }
  .proof-item h5{ font-size:15px; font-weight:600; margin-bottom:5px; }
  .proof-item p{ font-size:13.5px; color:var(--text-dim); }

  /* Industries */
  .ind-grid{ display:grid; grid-template-columns:repeat(6,1fr); gap:1px; background:var(--line-soft); border:1px solid var(--line-soft); border-radius:16px; overflow:hidden; }
  .ind{ background:var(--panel); padding:24px 16px; text-align:center; transition:background .2s; }
  .ind:hover{ background:var(--panel-2); }
  .ind .em{ font-size:22px; margin-bottom:10px; }
  .ind span{ font-size:13px; font-weight:600; }
  @media (max-width:900px){ .ind-grid{ grid-template-columns:repeat(3,1fr); } }
  @media (max-width:560px){ .ind-grid{ grid-template-columns:repeat(2,1fr); } }

  /* CTA */
  #cta{
    text-align:center; padding:130px 0;
    background:radial-gradient(ellipse 700px 400px at 50% 0%, rgba(124,92,255,0.16), transparent 70%);
  }
  #cta h2{ font-size:clamp(30px,4.4vw,52px); font-weight:700; max-width:760px; margin:20px auto 20px; line-height:1.1; }
  #cta p{ color:var(--text-dim); font-size:16.5px; max-width:520px; margin:0 auto 36px; }

  footer{ border-top:1px solid var(--line-soft); padding:50px 0 40px; }
  .foot-row{ display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:20px; }
  .foot-row .flinks{ display:flex; gap:26px; font-size:13.5px; color:var(--text-dim); }
  .foot-row .flinks a:hover{ color:var(--text); }
  .foot-small{ font-size:12.5px; color:var(--text-faint); margin-top:26px; }

  .reveal{ opacity:0; transform:translateY(18px); transition:opacity .7s ease, transform .7s ease; }
  .reveal.in{ opacity:1; transform:translateY(0); }
  @media (prefers-reduced-motion: reduce){
    .reveal{ opacity:1; transform:none; transition:none; }
    .hero-visual .scanline{ animation:none; }
  }

  a:focus-visible, button:focus-visible{ outline:2px solid var(--sky); outline-offset:3px; border-radius:4px; }

      `}</style>
      <section className="hero relative overflow-hidden z-10 hero-moving-gradient min-h-screen text-white">
        <div className="absolute inset-0 bg-black/25 pointer-events-none" />
        <div className="hero-glow absolute inset-0 pointer-events-none" />

        {/* Decorative floating blobs */}
        <div className="absolute top-1/4 left-10 w-72 h-72 bg-white/10 rounded-full blur-3xl animate-pulse pointer-events-none" style={{ animationDuration: '8s' }} />
        <div className="absolute bottom-1/4 right-10 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse pointer-events-none" style={{ animationDuration: '12s' }} />

        <div className="horizon z-10 relative"></div>
        <div className="wrap hero-grid z-10 relative mt-16 sm:mt-0">
          <div>
            <div className="eyebrow">Vigil AI × Aakash — one intelligence layer</div>
            <h1>Every camera. <span className="g1">Every drone.</span><br />One <span className="g2">line of sight.</span></h1>
            <p className="lede">Peopleclick turns your existing CCTV network and autonomous drone fleet into a single, real-time intelligence system — watching the floor and the field, so nothing that matters goes unseen.</p>
            <div className="hero-actions">
              <button href="#" onClick={handleBookDemo} className="btn-primary">Book a Demo →</button>
              <a href="#ground" className="btn-ghost" style={{ borderColor: "rgba(255,255,255,0.3)", color: "#fff", background: "rgba(255,255,255,0.05)" }}>Explore the Platform</a>
            </div>
          </div>
          <div className="hero-visual reveal in">
            <div className="hv-label sky">Sky · Aakash</div>
            <div className="hv-label grd">Ground · Vigil AI</div>
            <div className="scanline"></div>
            <div className="hv-core"><span>360°</span></div>
            <div className="hv-tag t1">PERSON <b>ID:1260</b></div>
            <div className="hv-tag t2">DRONE-04 <b>ACTIVE</b></div>
            <div className="hv-tag t3">ZONE INTEGRITY <b>98.2%</b></div>
            <div className="hv-tag ground t4">ANOMALY <b>0 OPEN</b></div>
          </div>
        </div>
      </section>

      <div className="statbar">
        <div className="wrap">
          <div className="stat"><div className="num pink">95%</div><div className="lbl">of CCTV footage today is never analyzed</div></div>
          <div className="stat"><div className="num">98.4%</div><div className="lbl">edge inference accuracy, sub-second</div></div>
          <div className="stat"><div className="num blue">10,000+</div><div className="lbl">nodes proven across distributed sites</div></div>
          <div className="stat"><div className="num">&lt;8s</div><div className="lbl">target alert latency, minutes to seconds</div></div>
        </div>
      </div>

      {/*  GROUND  */}
      <section id="ground">
        <div className="wrap">
          <div className="sec-head reveal">
            <div className="eyebrow ground">Ground Intelligence</div>
            <h2>Your CCTV was always watching. Now it understands what it sees.</h2>
            <p>Vigil AI is a vision analytics platform that reads your existing camera feeds in real time — detecting people, objects, anomalies and safety events, and turning them into alerts and business metadata instead of hours of unwatched footage.</p>
          </div>

          <div className="split reveal">
            <div>
              <h3 style={{ "fontSize": "20px", "marginBottom": "16px" }}>Built for two jobs at once</h3>
              <p style={{ "color": "var(--text-dim)", "fontSize": "14.5px", "lineHeight": "1.6" }}>Safety & compliance teams get real-time breach and hazard detection. Operations & CX teams get footfall, dwell time and behavioral insight — from the same footage, the same install.</p>
              <div className="metric-panel">
                <div className="metric-row"><span className="k">Deployment</span><span className="v">Cloud · On-prem · Hybrid</span></div>
                <div className="metric-row"><span className="k">Integrations</span><span className="v">ERP · CRM · VMS · HRMS</span></div>
                <div className="metric-row"><span className="k">Alerts</span><span className="v pink">SMS · Email · WhatsApp · Teams</span></div>
                <div className="metric-row"><span className="k">Detection speed</span><span className="v blue">98.4% @ edge</span></div>
              </div>
            </div>
            <div className="card-grid">
              <div className="card g"><div className="ic"><ExclamationTriangleIcon className="w-5 h-5 text-primaryBlue" /></div><h4>Bully &amp; fight detection</h4><p>Real-time violent behavior recognition with automated alert escalation to floor supervisors.</p></div>
              <div className="card g"><div className="ic"><ShieldCheckIcon className="w-5 h-5 text-primaryBlue" /></div><h4>PPE &amp; kit compliance</h4><p>Automated verification of safety gear, helmets and regulatory clothing across the floor.</p></div>
              <div className="card g"><div className="ic"><PlusCircleIcon className="w-5 h-5 text-primaryBlue" /></div><h4>Slip &amp; fall alerts</h4><p>Instant detection of personnel or customer accidents for rapid medical response.</p></div>
              <div className="card g"><div className="ic"><LockClosedIcon className="w-5 h-5 text-primaryBlue" /></div><h4>Unauthorised access</h4><p>Perimeter protection and bio-metric verification for restricted zone security.</p></div>
              <div className="card g"><div className="ic"><UsersIcon className="w-5 h-5 text-primaryBlue" /></div><h4>Footfall &amp; heatmaps</h4><p>People counting and density mapping for architectural and staffing decisions.</p></div>
              <div className="card g"><div className="ic"><ClockIcon className="w-5 h-5 text-primaryBlue" /></div><h4>Wait &amp; dwell time</h4><p>Queue duration tracking and engagement analytics, zone by zone.</p></div>
            </div>
          </div>
        </div>
      </section>

      {/*  SKY  */}
      <section id="sky">
        <div className="wrap">
          <div className="sec-head reveal">
            <div className="eyebrow sky">Sky Intelligence</div>
            <h2>Aakash — Sanskrit for sky. Command of everything above ground.</h2>
            <p>Autonomous AI-powered drones extend Peopleclick's vision beyond the fence line — sweeping blast zones, patrolling perimeters, and surveying terrain that ground teams and fixed cameras simply can't reach, all from one command platform.</p>
          </div>

          <div className="split reveal">
            <div className="card-grid" style={{ "gridTemplateColumns": "1fr 1fr" }}>
              <div className="card s"><div className="ic"><FireIcon className="w-5 h-5 text-primaryBlue" /></div><h4>Blasting clearance patrol</h4><p>Autonomous 500m+ danger-zone sweep with human, vehicle and animal detection — cuts clearance time up to 70%.</p></div>
              <div className="card s"><div className="ic"><SignalIcon className="w-5 h-5 text-primaryBlue" /></div><h4>Perimeter security patrol</h4><p>Scheduled or on-demand flights detecting unauthorized access, replacing CCTV blind spots.</p></div>
              <div className="card s"><div className="ic"><MapIcon className="w-5 h-5 text-primaryBlue" /></div><h4>Slope stability monitoring</h4><p>Photogrammetric &amp; thermal survey with AI deformation heatmaps — early warning before a crack is visible.</p></div>
              <div className="card s"><div className="ic"><CubeIcon className="w-5 h-5 text-primaryBlue" /></div><h4>Stockpile volumetric survey</h4><p>Automated 3D point-cloud volume calculation, ERP-ready within 60 minutes, accurate to ±0.5%.</p></div>
            </div>
            <div>
              <h3 style={{ "fontSize": "20px", "marginBottom": "16px" }}>The Aakash Command Platform</h3>
              <p style={{ "color": "var(--text-dim)", "fontSize": "14.5px", "lineHeight": "1.6" }}>One cloud-native hub orchestrating up to 12 drones per site, with live telemetry, incident management, and a 5-year immutable audit trail built for regulatory review.</p>
              <div className="metric-panel">
                <div className="metric-row"><span className="k">Global uptime</span><span className="v blue">99.99%</span></div>
                <div className="metric-row"><span className="k">Command latency</span><span className="v">&lt;50ms</span></div>
                <div className="metric-row"><span className="k">Flight time / charge</span><span className="v">45–55 min, up to 4,500m AMSL</span></div>
                <div className="metric-row"><span className="k">Compute</span><span className="v pink">NVIDIA Jetson Orin, 275 TOPS</span></div>
              </div>
            </div>
          </div>

          <div style={{ "marginTop": "70px" }} className="reveal">
            <h3 style={{ "fontSize": "19px", "marginBottom": "8px" }}>A five-phase path to go-live</h3>
            <p style={{ "color": "var(--text-dim)", "fontSize": "14px", "marginBottom": "8px" }}>Site assessment through continuous support — a defined runway, not an open-ended pilot.</p>
            <div className="phase-row">
              <div className="phase"><span className="pn">WEEKS 1–3</span><h5>Site &amp; Digital Twin</h5><p>2cm-resolution GIS model, hazard zone mapping, infrastructure survey.</p></div>
              <div className="phase"><span className="pn">WEEKS 3–5</span><h5>AI Calibration</h5><p>Site-specific visual signature capture and alert threshold tuning.</p></div>
              <div className="phase"><span className="pn">WEEKS 5–8</span><h5>Infrastructure</h5><p>Comms setup, control room, drone fleet commissioning.</p></div>
              <div className="phase"><span className="pn">WEEKS 8–10</span><h5>Pilot &amp; Validation</h5><p>Supervised missions, acceptance testing, operator certification.</p></div>
              <div className="phase"><span className="pn">WEEK 10+</span><h5>Go-Live &amp; Support</h5><p>Full handover, 24/7 remote support, quarterly reviews.</p></div>
            </div>
          </div>
        </div>
      </section>

      {/*  PROOF  */}
      <section id="proof">
        <div className="wrap">
          <div className="sec-head reveal">
            <div className="eyebrow">Proof, not promises</div>
            <h2>Deployed on the ground. Piloted in the field.</h2>
          </div>
          <div className="proof-grid reveal">
            <div className="quote-card">
              <div className="who"><span className="dot"></span><span>Case Study — QSR Franchise Group</span></div>
              <p className="big">Relocating condiment stations after Vigil AI flagged a bottleneck cut total customer journey time by 42 seconds — with zero cell-phone incidents in kitchen zones since activation.</p>
              <div className="kpi-mini">
                <div><div className="n">14</div><div className="l">Slip hazards flagged &amp; resolved pre-incident</div></div>
                <div><div className="n">42s</div><div className="l">Average wait-time reduction per customer</div></div>
              </div>
            </div>
            <div className="proof-list">
              <div className="proof-item"><span className="num">01</span><div><h5>Rajasthan sandstone belt — live deployment</h5><p>Aakash drones flying active multi-week field trials across open-cast terrain, with on-site ground control under government escort.</p></div></div>
              <div className="proof-item"><span className="num">02</span><div><h5>Blasting clearance: 25–40 min → 8–12 min target</h5><p>Parallel multi-zone sweeps replace sequential ground patrols, with a digital all-clear certificate replacing paper cards.</p></div></div>
              <div className="proof-item"><span className="num">03</span><div><h5>Danger-zone coverage: ~70% → 99.8% target</h5><p>Full-perimeter aerial coverage where ground teams physically cannot reach every blind spot.</p></div></div>
              <div className="proof-item"><span className="num">04</span><div><h5>Built for DGCA &amp; DGMS compliance</h5><p>Drone Rules 2021 alignment, Digital Sky UTM integration, and DGMS Circular No. 3 of 2023 documentation standards.</p></div></div>
            </div>
          </div>
        </div>
      </section>

      {/*  INDUSTRIES  */}
      <section id="industries">
        <div className="wrap">
          <div className="sec-head reveal">
            <div className="eyebrow">Where it's working</div>
            <h2>One platform, tuned to your floor.</h2>
          </div>
          <div className="ind-grid reveal">
            <div className="ind text-violet"><div className="em"><BuildingOfficeIcon className="w-8 h-8 mx-auto" /></div><span >Manufacturing</span></div>
            <div className="ind text-violet"><div className="em"><AcademicCapIcon className="w-8 h-8 mx-auto" /></div><span>Education</span></div>
            <div className="ind text-violet"><div className="em"><ShoppingBagIcon className="w-8 h-8 mx-auto" /></div><span>Retail</span></div>
            <div className="ind text-violet"><div className="em"><WrenchScrewdriverIcon className="w-8 h-8 mx-auto" /></div><span>Construction</span></div>
            <div className="ind text-violet"><div className="em"><BuildingStorefrontIcon className="w-8 h-8 mx-auto" /></div><span>Hospitality</span></div>
            <div className="ind text-violet"><div className="em"><BuildingOffice2Icon className="w-8 h-8 mx-auto" /></div><span>Healthcare</span></div>
            <div className="ind text-violet"><div className="em"><CogIcon className="w-8 h-8 mx-auto" /></div><span>Mining</span></div>
            <div className="ind text-violet"><div className="em"><BuildingOfficeIcon className="w-8 h-8 mx-auto" /></div><span>Enterprise Sites</span></div>
          </div>
        </div>
      </section>

      <section id="cta">
        <div className="wrap">
          <div className="eyebrow" style={{ "justifyContent": "center" }}>Let's map your site</div>
          <h2>See ground and sky intelligence <br />on your own footage.</h2>
          <p>Bring a camera feed or a site plan — we'll show you what Vigil AI and Aakash surface in the first 30 minutes.</p>
          <button href="#" onClick={handleBookDemo} className="btn-primary">Book a Demo →</button>
        </div>
      </section>
    </div>
  );
};

export default AILandingPage;
